import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';

// +++++++++++++++++ Change program status +++++++++++++++++

const changeProgramStatusRequest = async (
  props: paths['/program/{program_id}/{patient_id}/change-status']['post']['requestBody']['content']['application/json'] & {
    patient_id: string;
    program_id: string;
  },
) => {
  const response = axiosInstance.post<
    paths['/program/{program_id}/{patient_id}/change-status']['post']['requestBody']['content']['application/json'],
    paths['/program/{program_id}/{patient_id}/change-status']['post']['requestBody']['content']['application/json']
  >(`/program/${props.program_id}/${props.patient_id}/change-status`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useChangeProgramStatusMutation = ({
  patient_id,
  program_id,
}: {
  patient_id: string;
  program_id: string;
}) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['changeProgramStatus'],
    mutationFn: (
      props: paths['/program/{program_id}/{patient_id}/change-status']['post']['requestBody']['content']['application/json'],
    ) => changeProgramStatusRequest({ ...props, patient_id, program_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['patients'] }),
        client.invalidateQueries({ queryKey: ['patient', patient_id] }),
        client.invalidateQueries({
          queryKey: ['programStatusHistory', patient_id],
        }),
      ]),
  });

  return mutation;
};

// ++++++++++++++++++ GENERATE NEW WELCOME LETTERS +++++++++++++++++

const generateWelcomeLettersRequest = async ({
  program_id,
  record_id,
}: {
  program_id: string;
  record_id: string;
}) => {
  const response = axiosInstance.post<
    paths['/program/{program_id}/{record_id}/new-welcome-letter-batch']['post']['responses']['201']['content']
  >(`/program/${program_id}/${record_id}/new-welcome-letter-batch`);
  const data = await response;

  return data;
};

export const useGenerateWelcomeLettersMutation = (
  program_id: string,
  record_id: string,
) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['generateWelcomeLetters'],
    mutationFn: () => generateWelcomeLettersRequest({ record_id, program_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['welcomeLettersList', program_id, record_id],
        }),
      ]),
  });

  return mutation;
};

// ++++++++++++++++++ GENERATE NEW SUBMISSIONS +++++++++++++++++

const generateSubmissionsRequest = async ({
  program_id,
  record_id,
}: {
  program_id: string;
  record_id: string;
}) => {
  const response = axiosInstance.post<
    paths['/program/{program_id}/{record_id}/new-submission']['post']['responses']['201']['content']
  >(`/program/${program_id}/${record_id}/new-submission`);
  const data = await response;

  return data;
};

export const useGenerateSubmissionsMutation = (
  program_id: string,
  record_id: string,
) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['generateSubmission'],
    mutationFn: () => generateSubmissionsRequest({ record_id, program_id }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['submissionsList', program_id, record_id],
        }),
        client.invalidateQueries({
          predicate: ({ queryKey }) => queryKey.includes('patient'),
        }),
        client.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey.includes('programStatusHistory'),
        }),
      ]),
  });

  return mutation;
};

// +++++++++++++++++ Download welcomeLetters +++++++++++++++

export const welcomeLetterDownloadRequest = async (
  welcome_letter_batch_id: string,
) => {
  const response = await axiosInstance.get(
    `/program/welcome-letter-batch/${welcome_letter_batch_id}`,
    {
      headers: {
        'Content-type': 'application/zip',
      },
      responseType: 'blob',
    },
  );

  const data = (await response).data;

  return data;
};

export const useWelcomeLettersDownloadQuery = (
  welcome_letter_batch_id: string,
) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['welcomeLetterDownload'],
    mutationFn: () => welcomeLetterDownloadRequest(welcome_letter_batch_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['welcomeLettersList'],
        }),
      ]),
  });

  return mutation;
};

// +++++++++++++++++ Download submission +++++++++++++++

export const submissionDownloadRequest = async (submission_id: string) => {
  const response = await axiosInstance.get(
    `/program/submission/${submission_id}`,
    {
      headers: {
        'Content-type': 'application/xml',
      },
      responseType: 'blob',
    },
  );

  const data = (await response).data;

  return data;
};

export const useSubmissionDownloadQuery = (submission_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['submissionDownload'],
    mutationFn: () => submissionDownloadRequest(submission_id),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({
          queryKey: ['submissionsList'],
        }),
      ]),
  });

  return mutation;
};
